import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { FlagsContext } from 'src/context/flagsContext';
import { isElectron } from 'src/hooks/useElectron';
import { useAppSelector } from 'src/hooks/useStore';
import { selectCurrentUser } from 'src/store/storeUtils';

const ASSEMBLY_PROTOCOL = 'assembly';

/** time to wait before removing the iframe (in ms) */
const REDIRECT_TIMEOUT_MS = 2000;

interface DesktopAppRedirectState {
  isRedirecting: boolean;
  dismiss: () => void;
}

/**
 * Redirects to the Desktop app (if the user has enabled the setting)
 * by attempting to open a deep link to the current page. If the app is not installed,
 * the browser will simply ignore the request and stay on the current page.
 *
 * Returns `isRedirecting` (true while the deep-link attempt is active) and
 * a `dismiss` callback the user can invoke to cancel and stay in the browser.
 */
export const useDesktopAppRedirect = (): DesktopAppRedirectState => {
  const { fields: { openDeepLinksInDesktopApp = false } = {} } =
    useAppSelector(selectCurrentUser) ?? {};
  const { EnableDesktopAppAutolaunch } = useContext(FlagsContext);

  const shouldRedirect =
    !!EnableDesktopAppAutolaunch &&
    !!openDeepLinksInDesktopApp &&
    !isElectron();

  const [isRedirecting, setIsRedirecting] = useState(shouldRedirect);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsRedirecting(false);
    if (iframeRef.current) {
      iframeRef.current.remove();
      iframeRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!shouldRedirect) return;

    const { pathname, search, hash } = window.location;

    // creating a hidden iframe so the browser will try to open the deep link,
    // but won't navigate away from the current page if the app isn't installed
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${ASSEMBLY_PROTOCOL}://${pathname.replace(/^\/+/, '')}${search}${hash}`;
    document.body.appendChild(iframe);
    iframeRef.current = iframe;

    timerRef.current = setTimeout(() => {
      iframe.remove();
      iframeRef.current = null;
      timerRef.current = null;
      setIsRedirecting(false);
    }, REDIRECT_TIMEOUT_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      iframe.remove();
      iframeRef.current = null;
    };
  }, [shouldRedirect]);

  return { isRedirecting, dismiss };
};
